import React, { useEffect, useState } from "react";
import { Team } from "../../models/teamModel";
import useInvitationStore from "../../store/invitationsStore";
import { useTeamsStore } from "../../store/teamsStore";
import { IconButton } from "../IconButton";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { UserView } from "../UserView";
import { useConfirmationDialog } from "../ConfirmDialog";
import {
  classNames,
  getDisplayInviteRole,
  getGolfPlayerCategory,
  getUserFullName,
} from "../../utils/appUtils";
import { Text } from "../catalyst/text";
import { Heading } from "../catalyst/heading";

interface InviteToTeamProps {
  team: Team | null;
  inviteType: "inviteOwner" | "invitePlayer";
}

const InviteToTeam: React.FC<InviteToTeamProps> = ({ inviteType, team }) => {
  const {
    fetchOwnerInvitees,
    fetchPlayerInvitees,
    ownerInvitees,
    playerInvitees,
  } = useInvitationStore();

  const [filteredOwnerInvitees, setFilteredOwnerInvitees] = useState<any>([]);
  const [filteredPlayerInvitees, setFilteredPlayerInvitees] = useState<any>([]);
  const [search, setSearch] = useState("");

  const { openDialog } = useConfirmationDialog();

  const { showTeams, getTeamDetails } = useTeamsStore();

  const { createInvite } = useInvitationStore();

  useEffect(() => {
    if (!team) return;
    if (inviteType === "inviteOwner") {
      fetchOwnerInvitees(team.seasonId, team.id);
    } else if (inviteType === "invitePlayer") {
      fetchPlayerInvitees(team.seasonId, team.id);
    }
  }, [inviteType]);

  useEffect(() => {
    setFilteredOwnerInvitees(
      ownerInvitees?.filter((owner: any) =>
        getUserFullName(owner).toLowerCase().includes(search.toLowerCase())
      ) || []
    );
    setFilteredPlayerInvitees(
      playerInvitees?.filter((player: any) =>
        getUserFullName(player).toLowerCase().includes(search.toLowerCase())
      ) || []
    );
  }, [search, ownerInvitees, playerInvitees]);

  const handleInvite = (user: any, role: string) => {
    const fullName = getUserFullName(user);
    const inviteRole = getDisplayInviteRole(role);
    openDialog({
      title: `Invite ${inviteRole}`,
      message: `Are you sure you want to invite ${fullName} as a Team ${inviteRole}?`,
      onConfirm: async () => {
        if (!team) return;
        await createInvite(
          team.seasonId,
          team.id,
          role === "teamOwner" ? user.id : user.playerId,
          role
        );
        await getTeamDetails(team.id);
      },
    });
  };

  return (
    <div>
      <div className="flex justify-end px-2 mt-[-23px]">
        <IconButton onClick={() => showTeams()} Icon={XMarkIcon} text="" />
      </div>
      <div>
        <Heading className="text-xl font-semibold">
          Invite{" "}
          {inviteType === "inviteOwner"
            ? "to be a team owner"
            : "to be a team player"}
        </Heading>
        <div className="relative flex flex-1 h-8 mt-5">
          <label htmlFor="search-field" className="sr-only">
            Search
          </label>
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 dark:text-gray-200"
          />
          <input
            id="search-field"
            name="search"
            type="search"
            placeholder={
              inviteType === "inviteOwner"
                ? "Search userss..."
                : "Search players..."
            }
            onChange={(e) => setSearch(e.target.value)}
            className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 dark:text-gray-200 dark:placeholder:text-gray-200 bg-white dark:bg-gray-600 placeholder:text-gray-400 focus:ring-0 sm:text-sm rounded-md"
          />
        </div>
        <div
          className="mt-5 overflow-auto bg-white dark:bg-gray-800 shadow rounded mb-5 p-2"
          style={{ height: "calc(100vh - 300px)" }}
        >
          {inviteType === "inviteOwner" &&
            filteredOwnerInvitees.map((owner: any, index: number) => (
              <div
                key={owner.id}
                className={classNames(
                  "flex items-center justify-between p-2 rounded mb-2",
                  index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : ""
                )}
              >
                <UserView member={owner} />
                <button
                  className="px-3 py-1 bg-blue-500 text-white rounded"
                  onClick={() => handleInvite(owner, "teamOwner")}
                >
                  Invite
                </button>
              </div>
            ))}
          {inviteType === "invitePlayer" &&
            filteredPlayerInvitees.map((player: any, index: number) => (
              <>
                <div
                  key={player.playerId}
                  className={classNames(
                    "flex items-center justify-between p-2 rounded mb-2 gap-2",
                    index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
                    "hidden sm:flex"
                  )}
                >
                  <UserView member={player} />
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {getGolfPlayerCategory(player.ageCategory, player.gender)}
                  </div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {player.playerHandicap}
                  </div>
                  <button
                    className="px-3 py-1 bg-blue-500 text-white rounded"
                    onClick={() => handleInvite(player, "coreMember")}
                  >
                    Invite
                  </button>
                </div>

                <div
                  key={player.playerId}
                  className={classNames(
                    "flex items-center justify-between p-2 rounded mb-2",
                    index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
                    "sm:hidden"
                  )}
                >
                  <div className="w-full">
                    <UserView member={player} />
                    <div className="flex flex-row justify-between gap-3">
                    <div className="flex  gap-3">
                      <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {getGolfPlayerCategory(
                          player.ageCategory,
                          player.gender
                        )}
                      </div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {player.playerHandicap}
                      </div>
                      </div>
                      <button
                        className="px-3 py-1 bg-blue-500 text-white rounded"
                        onClick={() => handleInvite(player, "coreMember")}
                      >
                        Invite
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ))}

          {inviteType === "inviteOwner" &&
            filteredOwnerInvitees?.length === 0 && (
              <Text className="text-center">No users found to invite</Text>
            )}
          {inviteType === "inviteOwner" &&
            filteredOwnerInvitees?.length === 0 &&
            ownerInvitees?.length > 0 &&
            search && (
              <Text className="text-center">
                No users found for search term: "{search}"
              </Text>
            )}

          {inviteType === "invitePlayer" &&
            filteredPlayerInvitees?.length === 0 && (
              <Text className="text-center">No players found to invite</Text>
            )}
          {inviteType === "invitePlayer" &&
            filteredPlayerInvitees?.length === 0 &&
            playerInvitees?.length > 0 &&
            search && (
              <Text className="text-center">
                No players found for search term: "{search}"
              </Text>
            )}
        </div>
      </div>
    </div>
  );
};

export default InviteToTeam;
